/**
 * Expand group-setup schedule fields into local calendar session dates (YYYY-MM-DD).
 * Mirrors bot `expandGroupOccurrences` date selection (before timezone→ISO conversion).
 */
import { resolveGroupScheduleRecurrence, type GroupScheduleRecurrence } from '@unify/fhir-model/resources/group';

import { formatDateOnlyLocal, parseDateOnlyLocal } from '@/lib/dateHelpers';

import type { GroupEnrollmentMode, GroupSetupFormValues } from './types';

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

function addDaysYmd(ymd: string, days: number): string {
  const d = parseDateOnlyLocal(ymd);
  if (!d) return ymd;
  return formatDateOnlyLocal(new Date(d.getFullYear(), d.getMonth(), d.getDate() + days));
}

function daysBetweenYmd(a: string, b: string): number {
  const da = parseDateOnlyLocal(a);
  const db = parseDateOnlyLocal(b);
  if (!da || !db) return 0;
  return Math.round((db.getTime() - da.getTime()) / 86_400_000);
}

function weekdayNameFromYmd(ymd: string): string {
  const d = parseDateOnlyLocal(ymd);
  if (!d) return '';
  return WEEKDAY_NAMES[d.getDay()];
}

function daysInMonth(y: number, m: number): number {
  return new Date(y, m, 0).getDate();
}

function addMonthsKeepingDayOfMonth(ymd: string, months: number, preferredDom: number): string {
  const d = parseDateOnlyLocal(ymd);
  if (!d) return ymd;
  const total = d.getFullYear() * 12 + d.getMonth() + months;
  const ny = Math.floor(total / 12);
  const nm = total % 12;
  const day = Math.min(preferredDom, daysInMonth(ny, nm + 1));
  return formatDateOnlyLocal(new Date(ny, nm, day));
}

function normalizeDaysOfWeek(days: string[]): Set<string> {
  return new Set(
    days
      .map((day) => day.trim())
      .filter(Boolean)
      .map((day) => {
        const lower = day.toLowerCase();
        const match = WEEKDAY_NAMES.find((n) => n.toLowerCase() === lower);
        return match ?? day;
      }),
  );
}

export type GroupSetupSessionDateInput = Partial<
  Pick<
    GroupSetupFormValues,
    | 'startDate'
    | 'endDate'
    | 'enrollmentMode'
    | 'recurrence'
    | 'daysOfWeek'
    | 'sessionDates'
    | 'sessionCount'
  >
> & {
  startDate?: string;
};

/**
 * Session calendar days for room/device conflict checks during group setup.
 * Caps at `maxDates` so availability queries stay bounded.
 */
export function listGroupSetupSessionDates(
  values: GroupSetupSessionDateInput,
  opts?: {
    todayYmd?: string;
    openHorizonDays?: number;
    maxDates?: number;
  },
): string[] {
  const startDate = values.startDate?.trim();
  if (!startDate) return [];

  const todayYmd = opts?.todayYmd ?? formatDateOnlyLocal(new Date());
  const openHorizonDays = opts?.openHorizonDays ?? 30;
  const maxDates = opts?.maxDates ?? 60;
  const mode: GroupEnrollmentMode = values.enrollmentMode === 'open' ? 'open' : 'closed';
  const recurrence: GroupScheduleRecurrence = resolveGroupScheduleRecurrence({
    recurrence: values.recurrence,
  });

  let rangeStart = startDate;
  let rangeEnd: string;

  if (mode === 'open') {
    rangeStart = todayYmd > startDate ? todayYmd : startDate;
    rangeEnd = addDaysYmd(todayYmd, openHorizonDays);
    if (values.endDate && values.endDate < rangeEnd) rangeEnd = values.endDate;
  } else {
    if (!values.endDate) return [];
    rangeEnd = values.endDate;
  }

  if (rangeEnd < rangeStart) return [];

  const dateList: string[] = [];

  if (recurrence === 'custom') {
    for (const d of values.sessionDates ?? []) {
      if (d >= rangeStart && d <= rangeEnd) dateList.push(d);
    }
  } else if (recurrence === 'daily') {
    for (let cursor = rangeStart; cursor <= rangeEnd; cursor = addDaysYmd(cursor, 1)) {
      dateList.push(cursor);
    }
  } else if (recurrence === 'monthly') {
    const preferredDom = Number(startDate.split('-')[2]);
    let cursor = startDate;
    while (cursor < rangeStart) {
      cursor = addMonthsKeepingDayOfMonth(cursor, 1, preferredDom);
    }
    while (cursor <= rangeEnd) {
      dateList.push(cursor);
      cursor = addMonthsKeepingDayOfMonth(cursor, 1, preferredDom);
    }
  } else {
    const weeklyInterval = recurrence === 'biweekly' ? 2 : 1;
    const days = normalizeDaysOfWeek(values.daysOfWeek ?? []);
    if (days.size === 0) return [];
    for (let cursor = rangeStart; cursor <= rangeEnd; cursor = addDaysYmd(cursor, 1)) {
      const weekday = weekdayNameFromYmd(cursor);
      if (!days.has(weekday)) continue;
      const weekIndex = Math.floor(daysBetweenYmd(startDate, cursor) / 7);
      if (weekIndex < 0 || weekIndex % weeklyInterval !== 0) continue;
      dateList.push(cursor);
    }
  }

  // Mirrors the bot: a closed series stops at `sessionCount` even when endDate runs longer.
  const cap =
    mode === 'closed' && values.sessionCount && values.sessionCount > 0
      ? Math.min(values.sessionCount, maxDates)
      : maxDates;
  return dateList.slice(0, cap);
}

/** Local minutes-from-midnight for `HH:mm` (empty/invalid → null). */
export function hhmmToMinutes(hhmm: string | undefined): number | null {
  if (!hhmm) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}
